import React, { useState, useEffect } from "react";

const getToken = () => localStorage.getItem("access_token") || localStorage.getItem("token");

const ToolsPage = () => {
  const [tab, setTab] = useState("ea");
  const [apiKeys, setApiKeys] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyValue, setNewKeyValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [eaExpanded, setEaExpanded] = useState(true);
  const [vpsTab, setVpsTab] = useState("self");
  const [waitlist, setWaitlist] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [copied, setCopied] = useState(false);

  const C = {
    border: "1px solid var(--border)",
    muted: "var(--text-muted)",
    text: "var(--text-main)",
    faint: "var(--text-faint)",
  };

  useEffect(() => { fetchKeys(); fetchAccounts(); }, []);

  const fetchAccounts = async () => {
    try {
      const res = await fetch("/api/trader/accounts", {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      setAccounts(Array.isArray(data) ? data : (data.accounts || []));
    } catch (err) {}
  };

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/apikeys", {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      setApiKeys(Array.isArray(data) ? data : (data.keys || []));
    } catch (err) {
      setError("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  const generateKey = async () => {
    if (!newKeyName.trim()) return;
    setGenerating(true);
    setNewKeyValue(null);
    try {
      const res = await fetch("/api/apikeys", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ name: newKeyName, account_ids: accounts.map(a => a.id || a.ID) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to generate key");
      setNewKeyValue(data.key || data.api_key || data.token);
      setNewKeyName("");
      fetchKeys();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const generateEaKey = async () => {
    try {
      const res = await fetch("/api/apikeys", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ name: "EA Publisher Key", account_ids: accounts.map(a => a.id || a.ID) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      setNewKey(data.key || data.api_key || data.token);
      fetchKeys();
    } catch (err) { setError(err.message); }
  };

  const revokeKey = async (keyId, keyName) => {
    if (!confirm(`Revoke "${keyName}"? Any EA using this key will stop syncing.`)) return;
    try {
      const res = await fetch(`/api/apikeys/${keyId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error("Failed to revoke");
      fetchKeys();
    } catch (err) { setError("Failed to revoke key"); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const formatLastUsed = (d) => {
    if (!d) return <span style={{ color: "var(--text-faint)" }}>Never used</span>;
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return <span style={{ color: "var(--success)" }}>{mins}m ago</span>;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return <span style={{ color: "var(--success)" }}>{hrs}h ago</span>;
    return <span style={{ color: "var(--text-faint)" }}>{formatDate(d)}</span>;
  };

  const connColor = (s) => s === "connected" ? "var(--success)" : s === "disconnected" ? "var(--danger)" : "var(--warning)";
  const connBg = (s) => s === "connected" ? "var(--success-soft)" : s === "disconnected" ? "var(--danger-soft)" : "var(--warning-soft)";
  const connLabel = (s) => s === "connected" ? "Connected" : s === "disconnected" ? "Disconnected" : "Pending EA";

  const tabs = [
    { id: "ea",       label: "EA Publisher" },
    { id: "apikeys",  label: "API Keys" },
    { id: "accounts", label: "Accounts" },
  ];

  return (
    <div className="page">
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div>
          <h2>Tools & Integrations</h2>
          <p className="muted">Download EA Publisher, manage API keys, and manage your accounts.</p>
        </div>
      </div>

      {error && (
        <div style={{ padding: "10px 16px", background: "var(--danger-soft)", border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: "var(--radius-md)", color: "var(--danger)", fontSize: 13, marginBottom: 16,
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {error}
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}></button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 2, borderBottom: "1px solid var(--border-subtle)", marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "10px 18px", background: "transparent", border: "none",
            borderBottom: tab === t.id ? "2px solid var(--accent)" : "2px solid transparent",
            color: tab === t.id ? "var(--accent)" : "var(--text-muted)",
            fontWeight: tab === t.id ? 600 : 400, fontSize: 13, cursor: "pointer",
            marginBottom: -1, whiteSpace: "nowrap",
          }}>{t.label}</button>
        ))}
      </div>

      {/* Tab: EA Publisher */}
      {tab === "ea" && (
        <div>
          {/* EA Setup collapsible */}
          <div style={{ background: "var(--bg-surface)", border: C.border, borderRadius: "var(--radius-lg)", marginBottom: 12 }}>
            <div onClick={() => setEaExpanded(!eaExpanded)}
              style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>EA Setup & Download</span>
              <span style={{ fontSize: 12, color: C.muted, transition: "transform var(--t)", display: "inline-block",
                transform: eaExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
            </div>
            {eaExpanded && (
              <div style={{ padding: "0 24px 24px", borderTop: C.border }}>
                {/* VPS sub-tabs */}
                <div style={{ display: "flex", gap: 8, marginBottom: 16, paddingTop: 16 }}>
                  {[["self", "My Own VPS / PC"], ["managed", "Managed VPS"]].map(([v, l]) => (
                    <button key={v} onClick={() => setVpsTab(v)} style={{
                      padding: "7px 16px", borderRadius: "var(--radius-md)", fontSize: 12, fontWeight: 600,
                      cursor: "pointer", border: "1px solid",
                      borderColor: vpsTab === v ? "var(--accent)" : "var(--border)",
                      background: vpsTab === v ? "var(--accent-soft)" : "transparent",
                      color: vpsTab === v ? "var(--accent)" : "var(--text-muted)" }}>
                      {l}
                    </button>
                  ))}
                </div>

                {vpsTab === "self" && (
                  <div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                      {[
                        { n: "1", t: "Download EA", d: "Download the CrunchAlpha Publisher EA for your platform" },
                        { n: "2", t: "Install in MT4/MT5", d: "Copy .ex4 or .ex5 to your terminal Experts folder" },
                        { n: "3", t: "Generate EA Key", d: "Generate your unique key below and paste into EA input" },
                        { n: "4", t: "Enable AutoTrading", d: "Turn on AutoTrading in MT4/MT5 and attach EA to any chart" },
                      ].map(s => (
                        <div key={s.n} style={{ display: "flex", gap: 12, alignItems: "flex-start",
                          padding: "10px 12px", background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", border: C.border }}>
                          <div style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--accent)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>{s.n}</div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{s.t}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{s.d}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                      <a href="/ea/Crunchalpha_mt5_Publisher.ex5" download
                        style={{ flex: 1, padding: "10px 0", borderRadius: "var(--radius-md)",
                          background: "var(--accent-soft)", color: "var(--accent)",
                          border: "1px solid var(--accent-border)", fontSize: 12, fontWeight: 600,
                          textAlign: "center", textDecoration: "none", display: "block" }}>
                        Download EA MT5 (.ex5)
                      </a>
                      <a href="/ea/Crunchalpha_mt4_Publisher.ex4" download
                        style={{ flex: 1, padding: "10px 0", borderRadius: "var(--radius-md)",
                          background: "rgba(167,139,250,0.1)", color: "var(--purple,#a78bfa)",
                          border: "1px solid rgba(167,139,250,0.3)", fontSize: 12, fontWeight: 600,
                          textAlign: "center", textDecoration: "none", display: "block" }}>
                        Download EA MT4 (.ex4)
                      </a>
                    </div>
                    {/* EA Key */}
                    <div style={{ padding: "14px", background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", border: C.border }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>EA Key</div>
                          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                            {apiKeys.length > 0 ? "Key is active — paste into EA input" : "No key yet — generate one"}
                          </div>
                        </div>
                        <button onClick={generateEaKey}
                          style={{ padding: "6px 14px", borderRadius: "var(--radius-md)", border: C.border,
                            background: "transparent", color: C.muted, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          Generate New Key
                        </button>
                      </div>
                      {newKey && (
                        <div style={{ background: "var(--warning-soft)", border: "1px solid rgba(245,158,11,0.3)",
                          borderRadius: "var(--radius-md)", padding: "10px 12px" }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--warning)", marginBottom: 6 }}>
                            Save this key — shown only once
                          </div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <code style={{ flex: 1, fontSize: 11, color: C.text, wordBreak: "break-all",
                              background: "rgba(0,0,0,0.2)", padding: "6px 10px", borderRadius: "var(--radius-sm)" }}>{newKey}</code>
                            <button onClick={() => { navigator.clipboard.writeText(newKey); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                              style={{ padding: "6px 12px", borderRadius: "var(--radius-sm)", border: "none",
                                background: "var(--accent)", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                              {copied ? "Copied!" : "Copy"}
                            </button>
                          </div>
                        </div>
                      )}
                      {apiKeys.length > 0 && !newKey && (
                        <div style={{ padding: "6px 10px", background: "var(--success-soft)",
                          borderRadius: "var(--radius-sm)", border: "1px solid rgba(34,197,94,0.2)" }}>
                          <span style={{ fontSize: 11, color: "var(--success)", fontWeight: 600 }}>EA Key active</span>
                          <span style={{ fontSize: 11, color: C.faint, marginLeft: 8 }}>Generate new key only if compromised</span>
                        </div>
                      )}
                    </div>
                    {/* Security note */}
                    <div style={{ marginTop: 12, padding: "10px 14px", background: "var(--bg-elevated)",
                      borderRadius: "var(--radius-md)", border: C.border }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 4 }}>Security note</div>
                      <p style={{ fontSize: 11, color: C.faint, margin: 0, lineHeight: 1.6 }}>
                        EA Publisher only reads trade history and balance. It does not store your master password and cannot open or close positions.
                      </p>
                    </div>
                  </div>
                )}

                {vpsTab === "managed" && (
                  <div style={{ textAlign: "center", padding: "24px 0" }}>
                    {!waitlistDone ? (
                      <>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8 }}>Managed VPS — Coming Soon</div>
                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 20, maxWidth: 380, margin: "0 auto 20px" }}>
                          We will run the EA on our secure server — no setup needed on your end.
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20, textAlign: "left" }}>
                          {[
                            { t: "Never stored",     d: "Credentials used once to connect, never saved" },
                            { t: "Funds stay safe",  d: "Your money stays in your broker account" },
                            { t: "Disconnect anytime", d: "Remove access instantly from dashboard" },
                          ].map(x => (
                            <div key={x.t} style={{ padding: "12px", background: "var(--bg-elevated)",
                              borderRadius: "var(--radius-md)", border: C.border }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4 }}>{x.t}</div>
                              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{x.d}</div>
                            </div>
                          ))}
                        </div>
                        {!waitlist ? (
                          <button onClick={() => setWaitlist(true)}
                            style={{ padding: "10px 28px", borderRadius: "var(--radius-md)", border: "none",
                              background: "var(--accent)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                            Join Waitlist
                          </button>
                        ) : (
                          <div style={{ maxWidth: 320, margin: "0 auto" }}>
                            <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Enter your email to get notified:</div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <input value={waitlistEmail} onChange={e => setWaitlistEmail(e.target.value)}
                                placeholder="your@email.com" type="email"
                                style={{ flex: 1, padding: "8px 12px", background: "var(--bg-elevated)",
                                  border: C.border, borderRadius: "var(--radius-md)", color: C.text, fontSize: 12, outline: "none" }} />
                              <button onClick={() => { if (waitlistEmail) setWaitlistDone(true); }}
                                style={{ padding: "8px 16px", borderRadius: "var(--radius-md)", border: "none",
                                  background: "var(--accent)", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                                Join
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8 }}>You are on the list</div>
                        <div style={{ fontSize: 12, color: C.muted }}>
                          We will notify <b style={{ color: C.text }}>{waitlistEmail}</b> when Managed VPS is ready.
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab: API Keys */}
      {tab === "apikeys" && (
        <div style={{ background: "var(--bg-surface)", border: C.border, borderRadius: "var(--radius-lg)", padding: "20px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>API Keys</div>
            <span style={{ fontSize: 11, color: C.faint }}>For EA integration and custom dashboards</span>
          </div>

          {newKeyValue && (
            <div style={{ marginBottom: 16, padding: "12px 16px", background: "var(--success-soft)",
              border: "1px solid rgba(34,197,94,0.3)", borderRadius: "var(--radius-md)" }}>
              <p style={{ color: "var(--success)", fontWeight: 600, marginBottom: 4, fontSize: 13 }}>
                API Key generated — copy it now, it will not be shown again.
              </p>
              <code style={{ fontSize: 12, wordBreak: "break-all", background: "rgba(0,0,0,0.2)",
                padding: "8px 12px", borderRadius: "var(--radius-sm)", display: "block", color: C.text }}>
                {newKeyValue}
              </code>
              <button style={{ marginTop: 8, padding: "6px 14px", borderRadius: "var(--radius-md)",
                border: "none", background: "var(--accent)", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                onClick={() => navigator.clipboard.writeText(newKeyValue).then(() => alert("Copied!"))}>
                Copy to clipboard
              </button>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: C.muted, marginBottom: 6 }}>
                Key name
              </label>
              <input type="text" value={newKeyName} onChange={e => setNewKeyName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && generateKey()}
                placeholder='e.g. "EA - Exness Account"'
                style={{ width: "100%", padding: "10px 12px", background: "var(--bg)",
                  border: C.border, borderRadius: "var(--radius-md)", color: C.text,
                  fontSize: 13, boxSizing: "border-box", outline: "none" }} />
            </div>
            <button onClick={generateKey} disabled={generating || !newKeyName.trim()}
              style={{ padding: "10px 20px", background: "var(--accent)", border: "none",
                borderRadius: "var(--radius-md)", color: "#fff", fontSize: 13, fontWeight: 600,
                cursor: generating || !newKeyName.trim() ? "not-allowed" : "pointer",
                opacity: generating || !newKeyName.trim() ? 0.5 : 1, whiteSpace: "nowrap" }}>
              {generating ? "Generating..." : "Generate Key"}
            </button>
          </div>

          {loading ? (
            <p style={{ color: C.faint, fontSize: 13 }}>Loading keys...</p>
          ) : apiKeys.length === 0 ? (
            <p style={{ color: C.faint, fontSize: 13 }}>No API keys yet. Generate one to connect your EA.</p>
          ) : (
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                  {["Name", "Key Prefix", "Created", "Last Used", ""].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 600,
                      color: C.faint, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {apiKeys.map(k => (
                  <tr key={k.id} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                    <td style={{ padding: "10px 12px", color: C.text, fontWeight: 500 }}>{k.name}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <code style={{ fontSize: 11, background: "var(--bg-elevated)", padding: "2px 8px",
                        borderRadius: "var(--radius-sm)", color: C.muted }}>
                        {k.key_prefix || "crunch_..."}
                      </code>
                    </td>
                    <td style={{ padding: "10px 12px", color: C.faint }}>{formatDate(k.created_at)}</td>
                    <td style={{ padding: "10px 12px" }}>{formatLastUsed(k.last_used_at)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>
                      <button onClick={() => revokeKey(k.id, k.name)}
                        style={{ background: "var(--danger-soft)", border: "1px solid rgba(239,68,68,0.3)",
                          color: "var(--danger)", padding: "4px 12px", borderRadius: "var(--radius-sm)",
                          cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tab: Accounts */}
      {tab === "accounts" && (
        <div style={{ background: "var(--bg-surface)", border: C.border, borderRadius: "var(--radius-lg)", padding: "20px 24px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 16 }}>Connected Accounts</div>
          {accounts.length === 0 ? (
            <p style={{ color: C.faint, fontSize: 13 }}>No accounts connected yet.</p>
          ) : (
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                  {["Nickname", "Broker", "Platform", "Account No.", "Currency", "Status", "EA Connection"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 600,
                      color: C.faint, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accounts.map(a => (
                  <tr key={a.id || a.ID} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600, color: C.text }}>
                      <div>{a.nickname || "—"}</div>
                      {a.about && <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>
                        {a.about.slice(0, 40)}{a.about.length > 40 ? "..." : ""}
                      </div>}
                    </td>
                    <td style={{ padding: "10px 12px", color: C.muted }}>{a.broker || "—"}</td>
                    <td style={{ padding: "10px 12px", color: C.muted }}>{a.platform || "—"}</td>
                    <td style={{ padding: "10px 12px", color: C.muted }}>{a.account_number || "—"}</td>
                    <td style={{ padding: "10px 12px", color: C.muted }}>{a.currency || "USD"}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px",
                        borderRadius: "var(--radius-sm)",
                        background: a.status === "active" ? "var(--success-soft)" : "var(--danger-soft)",
                        color: a.status === "active" ? "var(--success)" : "var(--danger)" }}>
                        {a.status}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px",
                        borderRadius: "var(--radius-sm)", display: "inline-block",
                        background: connBg(a.connection_status),
                        color: connColor(a.connection_status) }}>
                        {connLabel(a.connection_status)}
                      </div>
                      <div style={{ fontSize: 10, color: C.faint, marginTop: 3 }}>
                        {a.connection_status === "connected" ? "Verified" :
                         a.connection_status === "disconnected" ? "EA not pushing data" : "Connect EA to activate"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default ToolsPage;
